/**
 * Compose / edit a single quiz question.
 *
 * Server-side validation enforces exactly-one-correct via Zod, so we
 * surface the count inline as guidance and disable Save when invalid.
 */
import type { QuizOptionInput, QuizQuestionCreate } from '@xsc/shared';
import { Button } from '../ui/button.js';
import { Input, Textarea } from '../ui/input.js';
import { FormField } from '../ui/form.js';

export interface QuestionDraft {
  question: string;
  explanation: string;
  points: number;
  options: QuizOptionInput[];
}

export function emptyQuestionDraft(): QuestionDraft {
  return {
    question: '',
    explanation: '',
    points: 1,
    options: [
      { option_text: '', is_correct: true, sort_order: 0 },
      { option_text: '', is_correct: false, sort_order: 1 },
    ],
  };
}

export function draftToPayload(d: QuestionDraft, sortOrder: number): QuizQuestionCreate {
  return {
    question: d.question.trim(),
    explanation: d.explanation.trim() || null,
    points: d.points,
    sort_order: sortOrder,
    options: d.options.map((o, i) => ({
      option_text: o.option_text,
      is_correct: o.is_correct,
      sort_order: i,
    })),
  };
}

export function QuestionEditor({
  value,
  onChange,
  onSave,
  onCancel,
  onDelete,
  saving,
}: {
  value: QuestionDraft;
  onChange: (d: QuestionDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  saving: boolean;
}): JSX.Element {
  const correctCount = value.options.filter((o) => o.is_correct).length;
  const isValid =
    value.question.trim().length > 0 &&
    value.options.length >= 2 &&
    value.options.length <= 6 &&
    correctCount === 1 &&
    value.options.every((o) => o.option_text.trim().length > 0);

  const setOption = (i: number, patch: Partial<QuizOptionInput>) => {
    onChange({
      ...value,
      options: value.options.map((o, j) => (i === j ? { ...o, ...patch } : o)),
    });
  };
  const setCorrect = (i: number) => {
    onChange({
      ...value,
      options: value.options.map((o, j) => ({ ...o, is_correct: i === j })),
    });
  };
  const addOption = () => {
    if (value.options.length >= 6) return;
    onChange({
      ...value,
      options: [...value.options, { option_text: '', is_correct: false, sort_order: value.options.length }],
    });
  };
  const removeOption = (i: number) => {
    if (value.options.length <= 2) return;
    onChange({ ...value, options: value.options.filter((_, j) => j !== i) });
  };

  return (
    <div className="space-y-3 border border-border rounded p-3 bg-surface">
      <FormField label="Question" required>
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            value={value.question}
            onChange={(e) => onChange({ ...value, question: e.target.value })}
          />
        )}
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Points">
          {(id) => (
            <Input
              id={id}
              type="number"
              min={0}
              value={value.points}
              onChange={(e) => onChange({ ...value, points: Number(e.target.value) || 0 })}
            />
          )}
        </FormField>
        <FormField label="Explanation (shown after submit)">
          {(id) => (
            <Input
              id={id}
              value={value.explanation}
              onChange={(e) => onChange({ ...value, explanation: e.target.value })}
            />
          )}
        </FormField>
      </div>

      <div>
        <div className="text-sm font-medium mb-1">
          Options{' '}
          <span className={correctCount === 1 ? 'text-ink-muted' : 'text-danger'}>
            ({correctCount} marked correct — exactly one required)
          </span>
        </div>
        <div className="space-y-1">
          {value.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                name="correct"
                checked={o.is_correct}
                onChange={() => setCorrect(i)}
                title="Mark correct"
              />
              <Input
                value={o.option_text}
                onChange={(e) => setOption(i, { option_text: e.target.value })}
                placeholder={`Option ${i + 1}`}
              />
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-border text-danger disabled:opacity-40"
                onClick={() => removeOption(i)}
                disabled={value.options.length <= 2}
                title="Remove option"
              >✕</button>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Button type="button" variant="secondary" onClick={addOption} disabled={value.options.length >= 6}>
            + Option ({value.options.length}/6)
          </Button>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        {onDelete && (
          <Button type="button" variant="secondary" onClick={onDelete}>
            Delete question
          </Button>
        )}
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="button" onClick={onSave} disabled={!isValid || saving}>
          {saving ? 'Saving…' : 'Save question'}
        </Button>
      </div>
    </div>
  );
}
